import React, { useState } from "react";
import { Check, Hand } from "lucide-react";

import { useSpacePlan } from "@/shared/api/spaces";
import Markdown from "@/shared/components/markdown/Markdown";
import SpacePlanSteps from "@/shared/components/SpacePlanSteps";
import { BrandButton, GhostButton } from "@/shared/components/ui-kit";

import type { ApiToolCall, RunPending, SpaceChange } from "@/shared/types";
import type { PendingReply } from "../types";

/** The apps tool: its `name` argument is the app action, its `args` that action's arguments. */
const APP_ACTION = "remote_tool_exec";

/** What would run — an app action by its slug, else the tool by name — and with what. */
function describe(call: ApiToolCall | undefined) {
  if (call?.name === APP_ACTION) {
    const { name, args, account } = call.args as { name?: string; args?: Record<string, unknown>; account?: string };
    return { what: name ?? call.name, args: args ?? {}, account: account ?? null };
  }
  return { what: call?.name ?? "a tool", args: call?.args ?? {}, account: null };
}

/** Definable's tool that saves a skill it drafted: the card shows the skill, not its JSON. */
const CREATE_SKILL = "create_skill";

/** `files` as the tool reads it: some models send the list as a JSON string. */
function draftFiles(value: unknown): { path?: unknown; text?: unknown }[] {
  if (typeof value === "string") {
    try {
      return draftFiles(JSON.parse(value));
    } catch {
      return [];
    }
  }
  return Array.isArray(value) ? value : [];
}

/** A drafted skill as it would be saved: its name, description, instructions and every other
    file's text — a person says yes to what they can read. */
function SkillDraft({ args }: { args: Record<string, unknown> }) {
  const files = draftFiles(args.files);
  return (
    <div className="mt-3 rounded-[12px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] divide-y divide-[var(--hairline)] overflow-hidden">
      <div className="px-3 py-2 min-w-0">
        <div className="font-mono text-[12.5px] font-medium text-[var(--text-primary)] break-all">{String(args.name ?? "")}</div>
        <div className="mt-0.5 text-[12px] leading-[1.5] text-[var(--text-secondary)] break-words">{String(args.description ?? "")}</div>
      </div>
      <div className="px-3 py-2 max-h-[200px] overflow-y-auto text-[12.5px]" aria-label="SKILL.md">
        <div className="section-label mb-1">SKILL.md</div>
        <Markdown text={String(args.instructions ?? "")} />
      </div>
      {files.length > 0 && (
        <div className="px-3 py-2 space-y-2">
          <div className="section-label">Other files</div>
          {files.map((f, i) => (
            <div key={`${i}:${String(f.path)}`} className="min-w-0">
              <div className="font-mono text-[11.5px] text-[var(--text-secondary)] break-all">{String(f.path ?? "")}</div>
              {typeof f.text === "string" ? (
                <pre aria-label={`Contents of ${String(f.path ?? "")}`} className="m-0 mt-1 max-h-[160px] overflow-auto rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] px-2.5 py-2 font-mono text-[11.5px] leading-[1.55] whitespace-pre-wrap break-words">
                  {f.text}
                </pre>
              ) : (
                <div className="mt-0.5 text-[11.5px] text-[var(--danger)]">No text: it can't be saved, and Definable is told so.</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Definable's tool that changes an agent's tables: the card shows the plan, step by step. */
const CHANGE_SPACE = "change_space";

/** The planned change to an agent's tables, as the plan reads now: each change, what it does
    to the rows, and what blocks it. The change is made only while this plan holds. */
function SpaceChanges({ args }: { args: Record<string, unknown> }) {
  const agentId = typeof args.agent_id === "string" ? args.agent_id : "";
  const changes = Array.isArray(args.changes) ? (args.changes as SpaceChange[]) : null;
  const plan = useSpacePlan(agentId, agentId ? changes : null);
  return (
    <div className="mt-3 rounded-[12px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] overflow-hidden max-h-[280px] overflow-y-auto">
      <SpacePlanSteps plan={plan.data} pending={plan.isFetching} error={plan.error ? plan.error.message : null} />
    </div>
  );
}

/** An argument as a person reads it: words as they are, anything else as indented JSON. */
const readable = (value: unknown) =>
  typeof value === "string" ? value : value == null ? "—" : typeof value === "object" ? JSON.stringify(value, null, 2) : String(value);

/* A call the agent's owner marked as needing approval, waiting for a yes or no.
   Approve runs it as asked; Deny tells the model it did not run. The note goes
   with either: it is the words on the answer, in place of "Approved." / "Denied.".
   Enter in the note does not decide — a keystroke never approves a send. Rendered
   in ComposerDock's Approvals tab. */
export default function PendingApproval({ pending, onAnswer, onStop, busy = false }: {
  pending: RunPending;
  onAnswer: (reply: PendingReply) => void;
  /** Ends the run instead of deciding. */
  onStop?: () => void;
  /** The answer is on its way. */
  busy?: boolean;
}) {
  const [note, setNote] = useState("");
  const said = note.trim();
  const { what, args, account } = describe(pending.call);
  const entries = Object.entries(args);

  return (
    <div className="p-4" role="group" aria-label={pending.title} aria-busy={busy}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 shrink-0 rounded-[8px] bg-[var(--warn-bg)] ring-1 ring-[var(--warn-ring)] flex items-center justify-center">
          <Hand className="w-[18px] h-[18px] text-[var(--warn-text)]" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="section-label !text-[var(--text-secondary)]">Needs your approval</div>
          <div className="mt-1 text-[13px] font-medium leading-5 text-[var(--text-primary)] break-words">{pending.title || "Allow this action?"}</div>
          <div className="mt-0.5 font-mono text-[10.5px] text-[var(--text-secondary)] break-all">{what}</div>
          {account && <div className="text-[11.5px] text-[var(--text-secondary)] break-all">as {account}</div>}
        </div>
      </div>

      {pending.call?.name === CREATE_SKILL ? (
        <SkillDraft args={pending.call.args} />
      ) : pending.call?.name === CHANGE_SPACE ? (
        <SpaceChanges args={pending.call.args} />
      ) : entries.length > 0 ? (
        <dl className="m-0 mt-3 rounded-[12px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] divide-y divide-[var(--hairline)] overflow-hidden">
          {entries.map(([key, value]) => (
            <div key={key} className="px-2.5 py-1.5 min-w-0">
              <dt className="font-mono text-[11px] text-[var(--text-secondary)]">{key}</dt>
              <dd className="m-0 mt-0.5 max-h-[120px] overflow-y-auto text-[12px] leading-[1.55] text-[var(--text-primary)] whitespace-pre-wrap break-words">
                {readable(value)}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="m-0 mt-3 text-[11.5px] text-[var(--text-quaternary)] italic">No arguments.</p>
      )}

      <div className="mt-3 flex items-center h-9 rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] focus-within:ring-[var(--hairline-focus)] px-2.5 min-w-0">
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-label="Note (optional)"
          placeholder="Add a note (optional)…"
          disabled={busy}
          className="flex-1 min-w-0 bg-transparent outline-none text-[16px] sm:text-[12px] placeholder:text-[var(--text-secondary)] tracking-[-0.005em]"
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t hairline pt-3">
        <BrandButton type="button" disabled={busy} onClick={() => onAnswer({ text: said || "Approved.", decision: true })} className="disabled:opacity-60">
          <Check className="w-3.5 h-3.5" strokeWidth={2} /> {busy ? "Submitting…" : "Approve"}
        </BrandButton>
        <GhostButton type="button" disabled={busy} onClick={() => onAnswer({ text: said || "Denied.", decision: false })} className="disabled:opacity-60">
          Deny
        </GhostButton>
        {onStop && (
          <button
            type="button"
            onClick={onStop}
            disabled={busy}
            className="press ml-auto text-[11.5px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] tracking-[-0.005em] disabled:opacity-40"
          >
            Stop run
          </button>
        )}
      </div>
    </div>
  );
}
